import { ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import LayoutContext from '../../../contexts/LayoutContext';
import AuthContext from '../../../contexts/AuthContext';
import FavoritosContext from '../../../contexts/FavoritosContext';
import NavegacionContext from '../../../contexts/NavegacionContext';
import getMenuBarItems from '../../../utils/menubar';
import {
    FAVORITOS_PANEL,
    MENU_GROUPS,
    MenuPanelKey,
    RECIENTES_PANEL,
    getGroupInfo,
    getPantallaActual,
    getPantallas,
} from '../../../utils/menuGroups';
import { MenuGroupKey } from '../../../models/layout/MenuBarItemModel';
import MenuBarItem, { FavoritoStar } from './MenuBarItem';

const esMovil = (): boolean => window.innerWidth < 768;

/**
 * Menú lateral en dos niveles: una barra angosta de íconos (siempre visible
 * en escritorio) con los bloques Operaciones / Gestión / Logística /
 * Favoritos / Configuración, y un panel que se despliega al elegir un bloque
 * y empuja el contenido. `isMenuBarActive` del LayoutContext indica si el
 * panel está abierto; en teléfono abre el cajón completo (barra + panel).
 */
const MenuBar = (): ReactElement | null => {
    const { isMenuBarActive, toggleMenuBar, menuSolicitado } = useContext(LayoutContext);
    const { userRoles } = useContext(AuthContext);
    const { favoritos } = useContext(FavoritosContext);
    const { recientes, borrarRecientes } = useContext(NavegacionContext);
    const history = useHistory();
    const { pathname, search } = useLocation();
    const menuRef = useRef<HTMLDivElement>(null);

    const items = useMemo(() => getMenuBarItems(userRoles), [userRoles]);
    const pantallas = useMemo(() => getPantallas(items), [items]);
    const grupoActual = getPantallaActual(pantallas, pathname)?.group;

    // Bloques con al menos una sección visible para los roles del usuario.
    const gruposVisibles = MENU_GROUPS.filter((g) => items.some((i) => i.group === g.key));
    const [panel, setPanel] = useState<MenuPanelKey>(grupoActual || 'operaciones');

    const esCliente = userRoles.includes('CLIENTE');

    useEffect(() => {
        if (esMovil()) {
            document.body.style.overflow = isMenuBarActive ? 'hidden' : '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isMenuBarActive]);

    // El cajón del teléfono se cierra al tocar fuera. En escritorio el panel
    // empuja el contenido, así que se queda abierto hasta que el usuario lo
    // cierre, elija una pantalla o presione Esc.
    useEffect(() => {
        if (!isMenuBarActive) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (!esMovil()) return;
            const target = e.target as HTMLElement;
            if (target.closest('.menu-button')) return;
            if (menuRef.current && !menuRef.current.contains(target)) {
                toggleMenuBar(false);
            }
        };
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') toggleMenuBar(false);
        };

        const id = setTimeout(() => {
            document.addEventListener('click', handleClickOutside, true);
        }, 100);
        document.addEventListener('keydown', handleEsc);

        return () => {
            clearTimeout(id);
            document.removeEventListener('click', handleClickOutside, true);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [isMenuBarActive, toggleMenuBar]);

    // Abierto desde la hamburguesa (teléfono): muestra el bloque de la página actual.
    useEffect(() => {
        if (isMenuBarActive && esMovil() && grupoActual) setPanel(grupoActual);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMenuBarActive]);

    // La ruta del sub-header pidió abrir un bloque (y sección).
    useEffect(() => {
        if (menuSolicitado) setPanel(menuSolicitado.group);
    }, [menuSolicitado]);

    if (esCliente) return null;

    const elegirPanel = (key: MenuPanelKey): void => {
        if (isMenuBarActive && panel === key && !esMovil()) {
            toggleMenuBar(false);
            return;
        }
        setPanel(key);
        toggleMenuBar(true);
    };

    const irAInicio = (): void => {
        toggleMenuBar(false);
        history.push('/');
    };

    const tituloPanel = (key: MenuPanelKey): string => {
        if (key === 'favoritos') return FAVORITOS_PANEL.title;
        if (key === 'recientes') return RECIENTES_PANEL.title;
        return getGroupInfo(key).title;
    };

    const irA = (href: string): void => {
        toggleMenuBar(false);
        history.push(href);
    };

    const railButton = (key: MenuPanelKey, label: string, icon: string, grupo?: MenuGroupKey) => (
        <button
            key={key}
            type="button"
            className={classNames('rail-btn', `rail-${key}`, {
                abierto: isMenuBarActive && panel === key,
                actual: grupo !== undefined && grupo === grupoActual,
            })}
            onClick={() => elegirPanel(key)}
            aria-pressed={isMenuBarActive && panel === key}
            title={tituloPanel(key)}
        >
            <i className={`fa ${icon}`} aria-hidden="true" />
            <span>{label}</span>
            {key === 'favoritos' && favoritos.length > 0 && <span className="rail-badge">{favoritos.length}</span>}
        </button>
    );

    const infoPanel = panel === 'favoritos' ? FAVORITOS_PANEL : panel === 'recientes' ? RECIENTES_PANEL : getGroupInfo(panel);
    const seccionesPanel = panel === 'favoritos' || panel === 'recientes' ? [] : items.filter((i) => i.group === panel);
    const hrefActual = `${pathname}${search}`;

    // Favoritos en el orden en que se marcaron; los que el usuario ya no
    // puede ver (perdió el rol o la pantalla se quitó) no se muestran.
    const listaFavoritos = favoritos
        .map((href) => pantallas.find((p) => p.href === href))
        .filter((p): p is NonNullable<typeof p> => !!p);

    return (
        <>
            <div
                className={classNames('menubar-backdrop', 'd-md-none', { active: isMenuBarActive })}
                onClick={() => toggleMenuBar(false)}
                aria-hidden="true"
            />

            <nav
                className={classNames('menubar', { active: isMenuBarActive })}
                ref={menuRef}
                aria-label="Menú de navegación"
            >
                <div className="menubar-rail">
                    <button
                        type="button"
                        className={classNames('rail-btn', { actual: pathname === '/' })}
                        onClick={irAInicio}
                        title="Inicio"
                    >
                        <i className="fa fa-home" aria-hidden="true" />
                        <span>Inicio</span>
                    </button>
                    {gruposVisibles
                        .filter((g) => g.key !== 'sistema')
                        .map((g) => railButton(g.key, g.railLabel, g.icon, g.key))}
                    {railButton('favoritos', FAVORITOS_PANEL.railLabel, FAVORITOS_PANEL.icon)}
                    {railButton('recientes', RECIENTES_PANEL.railLabel, RECIENTES_PANEL.icon)}
                    <span className="rail-spacer" />
                    {gruposVisibles
                        .filter((g) => g.key === 'sistema')
                        .map((g) => railButton(g.key, g.railLabel, g.icon, g.key))}
                </div>

                <div className={classNames('menubar-panel', `panel-${panel}`)}>
                    <div className="menubar-header">
                        <div className="menubar-heading">
                            <h2 className="menubar-title">{infoPanel.title}</h2>
                            <small>{infoPanel.description}</small>
                        </div>
                        <button
                            type="button"
                            className="menubar-close-btn"
                            onClick={() => toggleMenuBar(false)}
                            aria-label="Cerrar panel"
                            title="Cerrar panel (Esc)"
                        >
                            <i className="fa fa-angle-double-left" aria-hidden="true" />
                        </button>
                    </div>

                    <div className="menubar-container">
                        {panel === 'recientes' ? (
                            recientes.length > 0 ? (
                                <>
                                    {recientes.map((r) => (
                                        <div key={r.href} className={classNames('menu-favorito', 'fav-recientes', { active: r.href === hrefActual })}>
                                            <button type="button" className="menu-favorito-link" onClick={() => irA(r.href)}>
                                                <span className="menu-icon">
                                                    <i className={`fa ${getPantallaActual(pantallas, r.href.split('?')[0])?.icon || 'fa-file-o'}`} aria-hidden="true" />
                                                </span>
                                                <span className="menu-favorito-text">
                                                    <span>{r.titulo}</span>
                                                    {r.detalle && <small>{r.detalle}</small>}
                                                </span>
                                            </button>
                                        </div>
                                    ))}
                                    <button type="button" className="menu-recientes-borrar" onClick={borrarRecientes}>
                                        <i className="fa fa-trash-o" aria-hidden="true" /> Borrar lista
                                    </button>
                                </>
                            ) : (
                                <div className="menu-favoritos-vacio menu-recientes-vacio">
                                    <i className="fa fa-history" aria-hidden="true" />
                                    <p>Aún no hay páginas recientes.</p>
                                    <p>Las pantallas y fichas que abras (OP, solicitudes, liberaciones…) aparecerán aquí.</p>
                                </div>
                            )
                        ) : panel === 'favoritos' ? (
                            listaFavoritos.length > 0 ? (
                                listaFavoritos.map((p) => (
                                    <div
                                        key={p.href}
                                        className={classNames('menu-favorito', `fav-${p.group || 'sistema'}`, {
                                            active: getPantallaActual(pantallas, pathname)?.href === p.href,
                                        })}
                                    >
                                        <button
                                            type="button"
                                            className="menu-favorito-link"
                                            onClick={() => irA(p.href)}
                                        >
                                            <span className="menu-icon">
                                                <i className={`fa ${p.icon || 'fa-file-o'}`} aria-hidden="true" />
                                            </span>
                                            <span className="menu-favorito-text">
                                                <span>{p.name}</span>
                                                <small>{p.seccion}</small>
                                            </span>
                                        </button>
                                        <FavoritoStar href={p.href} name={p.name} />
                                    </div>
                                ))
                            ) : (
                                <div className="menu-favoritos-vacio">
                                    <i className="fa fa-star-o" aria-hidden="true" />
                                    <p>Aún no tienes favoritos.</p>
                                    <p>
                                        Marca la estrella junto a una pantalla, en cualquier bloque del menú
                                        o en la barra superior de la página, y aparecerá aquí.
                                    </p>
                                </div>
                            )
                        ) : (
                            seccionesPanel.map((item) => (
                                <MenuBarItem
                                    key={`${panel}-${item.href}`}
                                    menuBarItemModel={item}
                                    defaultOpen={seccionesPanel.length === 1}
                                    solicitud={menuSolicitado?.group === panel && menuSolicitado.seccion === item.name ? menuSolicitado.n : 0}
                                />
                            ))
                        )}
                    </div>
                </div>
            </nav>
        </>
    );
};

export default MenuBar;
